import {
  Button,
  HStack,
  SegmentControl,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useNavigate } from '@tanstack/react-router';

import { USER_ROLE_NAME } from '~/features/auth/roles';
import { authState } from '~/features/auth/state';
import { useAuthState } from '~/features/auth/useAuthState';
import {
  type ColorScheme,
  useColorScheme,
} from '~/features/settings/colorScheme';
import { useDevToolsSetting } from '~/features/settings/devTools';
import { useMockingSetting } from '~/features/settings/useMockingSetting';
import { buildTestId } from '~/utils/buildTestId';

import { Modal } from '../Modal';

const THEME_OPTION: ColorScheme[] = ['light', 'dark'];

type SettingsModalProps = {
  onClose: () => void;
};

const TEST_ID = 'settings-modal' as const;

export const SettingsModal = ({ onClose }: SettingsModalProps) => {
  const { colorScheme, toggleColorScheme } = useColorScheme();
  const { serverMocking, toggleServerMocking } = useMockingSetting();
  const { isAuthenticated, userInfo } = useAuthState();
  const { isDevToolsEnabled, toggleDevTools } = useDevToolsSetting();
  const navigate = useNavigate();
  const userRole = userInfo?.role
    ? USER_ROLE_NAME[userInfo.role]
    : 'Unavailable';

  return (
    <Modal
      testID={TEST_ID}
      title="Settings"
      primaryAction={{
        label: 'Close',
        onPress: onClose,
      }}
    >
      <Stack flexDirection="column" flex={1}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="$4"
        >
          {isAuthenticated ? (
            <>
              <Stack>
                <HStack gap="$1">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    Role:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: 'role',
                    })}
                  >
                    {userRole}
                  </Text>
                </HStack>
                <HStack gap="$1">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    User ID:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['user', 'id'],
                    })}
                  >
                    {userInfo?.userID}
                  </Text>
                </HStack>
                <HStack gap="$1">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    IDN ID:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['idn', 'id'],
                    })}
                  >
                    {userInfo?.idnID ?? 'Not assigned'}
                  </Text>
                </HStack>
              </Stack>
              <Button
                key="logout"
                aria-label="Logout"
                label="Logout"
                onPress={() => {
                  authState.reset();
                  void navigate({ to: '/login', replace: true });
                  onClose();
                }}
                size="medium"
                variant="secondary"
                marginBottom="$4"
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: ['logout', 'button'],
                })}
              />
            </>
          ) : (
            <>
              <Text
                type="$bodyBase.medium"
                marginBottom="$4"
                textAlign="center"
              >
                You are currently not logged in!
              </Text>
              <Button
                key="login"
                aria-label="login"
                label="Login"
                onPress={() => {
                  void navigate({ to: '/login' });
                  onClose();
                }}
                size="medium"
                variant="secondary"
                marginBottom="$4"
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: ['login', 'button'],
                })}
              />
            </>
          )}
        </Stack>

        <Text type="$h3.default" marginBottom="$4">
          Settings
        </Text>

        <Text type="$bodyBase.default" marginVertical="$4">
          Color Theme
        </Text>

        <SegmentControl
          createAccessibilityHint={() => 'Select a category'}
          selectedIndex={THEME_OPTION.indexOf(colorScheme)}
          items={THEME_OPTION}
          onChange={toggleColorScheme}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['color', 'theme', 'control'],
          })}
        />

        <Text type="$bodyBase.default" marginVertical="$4">
          Server Mocking
        </Text>

        <SegmentControl
          createAccessibilityHint={() => 'Select the server mocking state'}
          selectedIndex={serverMocking ? 0 : 1}
          items={['enabled', 'disabled']}
          onChange={toggleServerMocking}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['server', 'mocking', 'control'],
          })}
        />

        {process.env.VITEST ? null : (
          <>
            <Text type="$bodyBase.default" marginVertical="$4">
              Development Tools
            </Text>

            <SegmentControl
              createAccessibilityHint={() =>
                'Select the development tools state'
              }
              selectedIndex={isDevToolsEnabled ? 0 : 1}
              items={['enabled', 'disabled']}
              onChange={toggleDevTools}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['dev', 'tools', 'control'],
              })}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
};
